<template>
	<div class="guided-selection-page pb-12">
		<v-container>
			<sliders
				:height="$vuetify.breakpoint.smAndDown ? '240px' : '400px'"
				:contain="true"
				class="mb-6 rounded-xl overflow-hidden"
			></sliders>

			<v-skeleton-loader v-if="loading" type="article, actions" class="rounded-xl"></v-skeleton-loader>

			<v-stepper v-if="!loading" v-model="step" vertical class="guided-stepper rounded-xl" flat outlined>
				<!-- Date Selector -->
				<v-stepper-step :step="1" :complete="!!date" color="primary">
					<div class="d-flex align-center justify-space-between flex-grow-1">
						<h2 class="text-subtitle-1 font-weight-bold text-slate-900 mb-0">{{ "date" | trans }}</h2>
						<span class="caption font-weight-bold text-primary" v-if="date">
							{{ date | moment("calendar") }}
						</span>
					</div>
				</v-stepper-step>
				<v-stepper-content :step="1">
					<div class="stepper-content-box pa-4 rounded-xl mb-3">
						<default-field
							:props="{
								text: 'date',
								type: 'date',
								prependInnerIcon: 'mdi-calendar',
								allowedDates: onlySevenDays,
								outlined: true,
								dense: true,
								rounded: true,
							}"
							@input="() => step++"
							v-model="date"
						></default-field>
					</div>
				</v-stepper-content>

				<!-- Turno Selector -->
				<v-stepper-step :step="2" :complete="!!turno" color="primary">
					<div class="d-flex align-center justify-space-between flex-grow-1">
						<h2 class="text-subtitle-1 font-weight-bold text-slate-900 mb-0">{{ "horarios" | trans }}</h2>
						<span class="caption font-weight-bold text-primary" v-if="turno && api.objects.horarios">
							{{
								api.objects.horarios.find((h) => h.id === turno)
									? api.objects.horarios.find((h) => h.id === turno).full_name
									: ""
							}}
						</span>
					</div>
				</v-stepper-step>
				<v-stepper-content :step="2">
					<div class="stepper-content-box pa-4 rounded-xl mb-3">
						<default-field
							:props="{
								text: 'horarios',
								type: 'resource',
								resource: 'horarios',
								prependInnerIcon: 'mdi-clock-outline',
								allowedDates: onlySevenDays,
								outlined: true,
								dense: true,
								rounded: true,
							}"
							@input="() => step++"
							v-model="turno"
						></default-field>
						<v-btn text rounded small color="primary" @click="stepBack()" class="text-capitalize mt-2">
							<v-icon left small>mdi-arrow-left</v-icon>
							{{ "crud.back" | trans }}
						</v-btn>
					</div>
				</v-stepper-content>

				<!-- Job place Selector -->
				<v-stepper-step :step="3" :complete="!!job_place" color="primary">
					<div class="d-flex align-center justify-space-between flex-grow-1">
						<h2 class="text-subtitle-1 font-weight-bold text-slate-900 mb-0">{{ "job places" | trans }}</h2>
						<span class="caption font-weight-bold text-primary" v-if="job_place && api.objects.puestos">
							{{
								api.objects.puestos.find((h) => h.id === job_place)
									? api.objects.puestos.find((h) => h.id === job_place).full_name
									: ""
							}}
						</span>
					</div>
				</v-stepper-step>
				<v-stepper-content :step="3">
					<div class="stepper-content-box pa-4 rounded-xl mb-3">
						<default-field
							:props="{
								text: 'job place',
								type: 'resource',
								resource: 'puestos',
								prependInnerIcon: 'mdi-map-marker-outline',
								allowedDates: onlySevenDays,
								outlined: true,
								dense: true,
								rounded: true,
							}"
							@input="() => step++"
							v-model="job_place"
						></default-field>
						<v-btn text rounded small color="primary" @click="stepBack()" class="text-capitalize mt-2">
							<v-icon left small>mdi-arrow-left</v-icon>
							{{ "crud.back" | trans }}
						</v-btn>
					</div>
				</v-stepper-content>

				<!-- Categories Steps -->
				<template v-for="(category, index) in categories">
					<v-stepper-step :key="category.id + '-header'" :complete="step > index + 4" :step="index + 4" color="primary">
						<div class="d-flex align-center justify-space-between flex-grow-1">
							<div class="d-flex align-center">
								<v-avatar size="28" class="mr-2" v-if="category.image_url">
									<v-img :src="category.image_url" contain></v-img>
								</v-avatar>
								<div>
									<h2 class="text-subtitle-1 font-weight-bold text-slate-900 mb-0">{{ category.full_name }}</h2>
									<small class="caption text-secondary-custom" v-if="category.descripción">{{
										category.descripción
									}}</small>
								</div>
							</div>
							<span class="caption font-weight-bold text-primary" v-if="selectedProducts[index]">
								{{ selectedProducts[index].name }}
							</span>
						</div>
					</v-stepper-step>
					<v-stepper-content :key="category.id + '-body'" :step="index + 4">
						<div class="stepper-content-box pa-4 rounded-xl mb-3">
							<v-row>
								<v-col v-for="product in perCategoria(category.id)" :key="product.id" cols="12" sm="6" md="3">
									<v-card
										class="guided-item-card pa-3 text-center rounded-xl"
										flat
										outlined
										@click="selectProduct(product)"
									>
										<v-img :src="product.image_url" contain height="120" class="mb-2"></v-img>
										<h4 class="caption font-weight-bold text-slate-800 text-truncate">{{ product.name }}</h4>
									</v-card>
								</v-col>
								<v-col cols="12" sm="6" md="3">
									<v-card
										class="guided-item-card pa-3 text-center rounded-xl"
										flat
										outlined
										@click="selectProduct(null)"
									>
										<v-avatar size="60" color="#f1f5f9" class="my-4">
											<v-icon color="#94a3b8">mdi-close-circle-outline</v-icon>
										</v-avatar>
										<h4 class="caption font-weight-bold text-slate-600">{{ "no select" | trans }}</h4>
									</v-card>
								</v-col>
							</v-row>
							<v-btn text rounded small color="primary" @click="stepBack()" class="text-capitalize mt-3">
								<v-icon left small>mdi-arrow-left</v-icon>
								{{ "crud.back" | trans }}
							</v-btn>
						</div>
					</v-stepper-content>
				</template>

				<!-- Confirm Order Step -->
				<v-stepper-step :step="categories.length + 4" color="primary">
					<div class="d-flex align-center">
						<h2 class="text-subtitle-1 font-weight-bold text-slate-900 mb-0">
							{{ "confirm" | trans }} {{ "order" | trans }}
						</h2>
					</div>
				</v-stepper-step>
				<v-stepper-content :step="categories.length + 4">
					<div class="stepper-content-box pa-4 rounded-xl mb-3">
						<h4 class="caption font-weight-bold text-secondary-custom mb-3">
							{{ "selected" | trans }} {{ "products" | trans }}:
						</h4>
						<div v-for="(product, index) in selectedProducts" :key="index">
							<div class="d-flex align-center justify-space-between py-2 border-b" v-if="product">
								<div class="d-flex align-center">
									<v-avatar size="36" color="#f8fafc" class="mr-3 rounded-lg border">
										<v-img :src="product.image_url" contain></v-img>
									</v-avatar>
									<div>
										<div class="caption font-weight-bold text-slate-800">{{ product.name }}</div>
										<div class="caption text-secondary-custom">{{ getCategory(product.categoria_id).full_name }}</div>
									</div>
								</div>
								<div class="caption font-weight-bold text-slate-900">Cant: 1</div>
							</div>
						</div>

						<div class="d-flex justify-space-between align-center flex-wrap pt-4">
							<v-btn text rounded small color="primary" @click="stepBack()" class="text-capitalize">
								<v-icon left small>mdi-arrow-left</v-icon>
								{{ "crud.back" | trans }}
							</v-btn>
							<div>
								<v-btn text rounded small color="error" @click="cancelOrder" class="text-capitalize mr-2">
									{{ "crud.cancel" | trans }}
								</v-btn>
								<v-btn depressed rounded color="success" class="text-capitalize font-weight-bold" @click="saveOrder">
									<v-icon left small>mdi-check</v-icon>
									{{ "confirm" | trans }} {{ "order" | trans }}
								</v-btn>
							</div>
						</div>
					</div>
				</v-stepper-content>
			</v-stepper>
		</v-container>
	</div>
</template>
<script>
import moment from "moment";
import DefaultField from "@/components/main/DefaultField.vue";
import Sliders from "@/components/main/Sliders";
export default {
	components: {
		DefaultField,
		Sliders,
	},
	async mounted() {
		this.getData();
	},
	data() {
		return {
			products: [],
			categories: [],
			selectedProducts: [],
			step: 1,
			loading: false,
			date: null,
			job_place: null,
			turno: null,
		};
	},
	methods: {
		async getData() {
			this.loading = true;
			this.products = await this.api.load("productos?scope[active]=1");
			this.categories = await this.api.load("categorias-productos?whereNull[]=parent_id");
			this.loading = false;
		},
		perCategoria(categoriaId) {
			return this.products.filter((product) => product.categoria_id === categoriaId);
		},
		getCategory(categoriaId) {
			return (
				this.categories.find((category) => category.id === categoriaId) || {
					full_name: "No category",
					color: "black",
				}
			);
		},
		selectProduct(product) {
			this.selectedProducts.push(product);
			this.step++;
		},
		onlySevenDays(date) {
			const sevenDaysFromNow = moment().add(7, "days");
			return moment(date).isBefore(sevenDaysFromNow) && moment(date).isAfter(moment());
		},
		async saveOrder() {
			const items = this.selectedProducts
				.filter((i) => i)
				.map((i) => {
					return {
						producto_id: i.id,
						cantidad: 1,
						precio: i.precio,
						impuesto: i.impuesto,
						advanced_stock_index: i.advanced_stock_index,
						name: i.name,
						description: i.description,
						referencia: i.referencia,
						image_id: i.image_id,
					};
				});
			const payload = {
				user_id: this.api.user.id,
				cliente_id: this.api.user.cliente_id || 1,
				direccion_envio: this.api.user.direccion,
				items: items,
				estado: "Pedido",
				fecha_entrega: this.date ? moment(this.date).utc().format() : undefined,
				facturar: false,
			};
			await this.api.post("pedidos", payload);
			this.$router.push({ name: "orders" });
		},
		cancelOrder() {
			this.selectedProducts = [];
			this.step = 1;
			this.date = null;
			this.job_place = null;
			this.turno = null;
		},
		stepBack() {
			this.step--;
			if (this.step < 1) {
				this.step = 1;
			}
			if (this.step === 1) {
				this.date = null;
			}
			if (this.step === 2) {
				this.turno = null;
			}
			if (this.step === 3) {
				this.job_place = null;
			}
			if (this.step > 3 && this.selectedProducts.length > 0) {
				this.selectedProducts.pop();
			}
		},
	},
};
</script>

<style scoped>
.guided-selection-page {
	background-color: #f8fafc;
	min-height: 100vh;
}

.guided-stepper {
	background-color: #ffffff !important;
	border: 1px solid #eef2f6 !important;
}

.stepper-content-box {
	background-color: #f8fafc;
	border: 1px solid #f1f5f9;
}

.guided-item-card {
	background-color: #ffffff !important;
	border: 1px solid #e2e8f0 !important;
	cursor: pointer;
	transition: all 0.2s ease;
}

.guided-item-card:hover {
	border-color: #3b82f6 !important;
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(59, 130, 246, 0.08) !important;
}

.border-b {
	border-bottom: 1px solid #f1f5f9;
}

.border {
	border: 1px solid #e2e8f0;
}

.text-slate-900 {
	color: #0f172a;
}

.text-slate-800 {
	color: #1e293b;
}

.text-slate-600 {
	color: #475569;
}

.text-secondary-custom {
	color: #64748b;
}
</style>
